import { composeValidators, email, required } from '@community_dev/pixels'
import { Button, Input, Space, Typography } from 'antd'
import type { JSX } from 'react'
import { Field, Form } from 'react-final-form'
import styled from 'styled-components'

import { RoleFields } from './RoleFields'

import { Client, Role } from 'api/clients'
import { StyledModal } from 'shared/components/Modal'
import { getPrimaryRole } from 'utils/roles'

const StyledInput = styled(Input)`
  width: 275px;
`

type InviteFormValues = { email: string; roles: Role[] }

type InviteFormProps = {
  onSubmit(values: InviteFormValues): any
  allRoles: Role[]
  primaryRoles: Role[]
  isDownloadEnabled: boolean
  client: Client
  isVisible: boolean
  setIsVisible(arg: boolean): any
  title: string
  buttonName: string
  hasDropdown?: boolean
  isButtonDisabled?: boolean
  buttonDisabledMessage?: string
}

export function InviteForm({
  onSubmit,
  allRoles,
  primaryRoles,
  isDownloadEnabled,
  client,
  isVisible,
  setIsVisible,
  title,
  buttonName = 'Invite',
  hasDropdown = true,
  isButtonDisabled = false,
  buttonDisabledMessage = '',
}: InviteFormProps): JSX.Element {
  const defaultRole = primaryRoles[0]
  return (
    <Form<InviteFormValues>
      initialValues={{
        roles: defaultRole ? [defaultRole] : [],
      }}
      onSubmit={onSubmit}
      render={({ handleSubmit, values, submitting }) => (
        <StyledModal
          centered
          footer={
            <Button
              data-testid="invite-submit-button"
              disabled={isButtonDisabled || (hasDropdown && !getPrimaryRole(values.roles))}
              loading={submitting}
              onClick={handleSubmit}
              size="large"
              type="primary"
            >
              {buttonName}
            </Button>
          }
          onCancel={() => setIsVisible(false)}
          open={isVisible}
          title={title}
        >
          <form onSubmit={handleSubmit}>
            <Space
              css={`
                width: 100%;
              `}
              direction="vertical"
            >
              <Field name="email" validate={composeValidators(required, email)}>
                {({ input, meta }) => (
                  <>
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label htmlFor="email">Email Address</label>
                    <StyledInput id="email" placeholder="Invite someone..." size="large" type="email" {...input} />
                    {!meta.active && meta.touched && meta.modified && meta.error && (
                      <div>
                        <Typography.Text aria-live="polite" type="danger">
                          Email {meta.error}
                        </Typography.Text>
                      </div>
                    )}
                  </>
                )}
              </Field>
              {isButtonDisabled && <Typography.Text type="danger">{buttonDisabledMessage}</Typography.Text>}
              {hasDropdown && (
                <Field<Role[]> name="roles">
                  {({ input }) => (
                    <RoleFields
                      allRoles={allRoles}
                      client={client}
                      isDownloadEnabled={isDownloadEnabled}
                      onChange={input.onChange}
                      primaryRoles={primaryRoles}
                      selectedRoles={input.value}
                    />
                  )}
                </Field>
              )}
            </Space>
          </form>
        </StyledModal>
      )}
    />
  )
}
